import {
  type NexoyaContentBiddingStrategy,
  type NexoyaContentFilter,
  NexoyaContentFilterFieldName,
  type NexoyaContentFilterInput,
  NexoyaContentFilterOperator,
  type NexoyaContentLabelAssignment,
  type NexoyaContentLabelCategory,
  type NexoyaContentLabelCategoryValue,
  type NexoyaContentLabelStageCounts,
  NexoyaContentLabelStatus,
  type NexoyaContentV2,
  type NexoyaProvider,
} from '../../../../types';

import { normalizeCategoryColor } from '../../../../utils/labelChipColors';
import { toContentFilterInput } from '../labelsAdapter';
import type {
  LabelAssignmentRow,
  LabelCategory,
  LabelDefinition,
  LabelFilterClause,
  LabelPreviewRow,
  LabelStage,
  LabelStageCounts,
  LabelableContent,
} from '../types';

/**
 * The boundary between the Content Labels v2 wire types and the FE-owned domain
 * types in `../types`.
 *
 * Deliberately free of React and Apollo so it runs in the `unit` (node) vitest
 * project — the GraphQL documents live in `src/graphql/contentLabels/`, and the
 * repository (`./labelsRepository`) and the manual-assignment hooks call into
 * this file, never the other way round. The payload types below are the
 * SELECTIONS those documents make, picked from the generated `Nexoya*` types,
 * because a generated type promises every field of the object and a response
 * only carries what was asked for.
 */

/**
 * What the category list selects. A full `NexoyaContentLabelCategory` is
 * assignable to this, so `toLabelCategory` still accepts one; `color` stays
 * optional because it is normalized here whatever arrives (`normalizeCategoryColor`).
 */
export type ContentLabelCategoryPayload = Pick<NexoyaContentLabelCategory, 'contentLabelCategoryId' | 'name'> & {
  teamId?: NexoyaContentLabelCategory['teamId'];
  color?: NexoyaContentLabelCategory['color'] | null;
  values?: NexoyaContentLabelCategoryValue[] | null;
};

/** The `ContentV2` fields a labelling table row is built from (`LABELABLE_CONTENT_FRAGMENT`). */
export type LabelableContentPayload = Pick<NexoyaContentV2, 'contentId' | 'title' | 'objective'> & {
  parent?: Pick<NexoyaContentV2, 'contentId' | 'title'> | null;
  provider?: Pick<NexoyaProvider, 'provider_id' | 'name'> | null;
  biddingStrategy?: Pick<NexoyaContentBiddingStrategy, 'type'> | null;
};

/**
 * A label as a row refers to it (`CONTENT_LABEL_VALUE_REF_FRAGMENT`): the id only.
 * A row never carries a label's name — the table names it from the category's
 * labels, so a rename shows everywhere at once and no row can write a stale one
 * back over it.
 */
export type LabelValueRefPayload = Pick<NexoyaContentLabelCategoryValue, 'contentLabelCategoryValueId'>;

/** One node of a stage connection (`ContentLabelAssignmentFields`). */
export type LabelAssignmentPayload = Pick<NexoyaContentLabelAssignment, 'labelStatus'> & {
  assignment?: LabelValueRefPayload | null;
  matches?: LabelValueRefPayload[] | null;
  content: LabelableContentPayload;
};

/** One `filterContents` row of the rule preview, with its per-category label state. */
export type LabelPreviewContentPayload = LabelableContentPayload & {
  labelAssignments?: Array<
    Pick<NexoyaContentLabelAssignment, 'labelStatus'> & {
      contentLabelCategory: Pick<NexoyaContentLabelCategory, 'contentLabelCategoryId'>;
      matches?: LabelValueRefPayload[] | null;
      assignment?: LabelValueRefPayload | null;
    }
  > | null;
};

/** One category of the stage-counts document: the number of contents in each stage. */
export type LabelStageCountsPayload = Pick<NexoyaContentLabelCategory, 'contentLabelCategoryId'> & {
  contentsByAssignmentStage?: { counts?: Partial<Pick<NexoyaContentLabelStageCounts, LabelStage>> | null } | null;
};

/**
 * `provider.name` is a translation KEY on the wire (`{provider.facebook}`), and
 * translations live in a React store — so the mapper takes the translator rather
 * than reaching for the store and stopping being pure.
 */
export type TranslateProviderName = (name: string) => string;

/**
 * What a person reads for a provider, or null when there is nothing readable.
 *
 * The ONE rule for it: the content rows and the Channels & Accounts pick list
 * both go through here, so a provider cannot read one way in a row and another
 * on a rule chip. A provider without a name is never handed to the translator —
 * `translate` answers a missing key with the literal "No data", which would read
 * as a provider of that name — and a key with no translation (which `translate`
 * strips down to '') is no name either.
 */
export function toProviderDisplayName(
  name: string | null | undefined,
  translateProviderName: TranslateProviderName,
): string | null {
  return name ? translateProviderName(name) || null : null;
}

/** Re-exported so the data layer has one import for the whole mapping boundary. */
export { toContentFilterInput };

// ── categories & labels ─────────────────────────────────────────────────────

/**
 * Keywords are `string[]` in the UI, but a `ContentFilterValue` is a union of
 * scalar shapes and the backend picks whichever fits the field (`numberArr` for
 * `displayProviderId`, `string` for a single `eq`). Reading only `stringArr`
 * would silently drop those rules and render the label as manual-only.
 */
function toKeywords(filter: NexoyaContentFilter): string[] {
  const value = filter?.value;
  if (!value) return [];
  if (value.stringArr?.length) return value.stringArr;
  if (value.numberArr?.length) return value.numberArr.map(String);
  if (value.string != null && value.string !== '') return [value.string];
  if (value.number != null) return [String(value.number)];
  if (value.date != null && value.date !== '') return [String(value.date)];
  return [];
}

/**
 * The hidden clause decision 5 persists a label's exclusions as: `contentId ne
 * [ids]`. Only the `ne` form — a positive `contentId` clause is a rule, and
 * hiding it would make the label match contents its chips never mention.
 */
function isExclusionClause(filter: NexoyaContentFilter): boolean {
  return (
    filter?.fieldName === NexoyaContentFilterFieldName.ContentId && filter?.operator === NexoyaContentFilterOperator.Ne
  );
}

/**
 * `ContentFilter` has no id on the wire, but the clause editor keys its chips and
 * its "which clause is open" state off `clauseId`. Deriving it from the owning
 * value id plus the clause's position keeps it stable across refetches, which a
 * uuid minted per render would not be. The position is the one ON THE WIRE, so a
 * rule clause keeps its id whether or not an exclusion clause sits before it.
 */
function toFilterClauses(value: NexoyaContentLabelCategoryValue): LabelFilterClause[] {
  return (value.filters ?? []).flatMap((filter, index) =>
    isExclusionClause(filter)
      ? []
      : [
          {
            clauseId: `${value.contentLabelCategoryValueId}:${index}`,
            fieldName: filter.fieldName,
            operator: filter.operator,
            keywords: toKeywords(filter),
          },
        ],
  );
}

/**
 * Every exclusion clause's ids, as the id-strings the UI keys contents by. More
 * than one clause is merged (and de-duplicated) rather than trusting the FE to
 * have been the only writer.
 */
function toExcludedContentIds(value: NexoyaContentLabelCategoryValue): string[] {
  const ids = (value.filters ?? []).filter(isExclusionClause).flatMap(toKeywords);
  return [...new Set(ids)];
}

/**
 * `labelCategoryId` comes from the CATEGORY being mapped, not from the value's
 * own `contentLabelCategoryId`. They always agree on the wire, but the UI groups
 * labels by the id of the card they were rendered under, so taking the parent's
 * id makes a disagreement impossible rather than a label that renders in one
 * card and saves against another.
 *
 * The exclusion clause is split OUT of `filters` into `excludedContentIds`: it is
 * not a rule the user wrote, so it must not surface as a rule chip or count
 * towards the "+N" chip overflow. `toLabelValueFiltersInput` puts it back.
 */
export function toLabelDefinition(value: NexoyaContentLabelCategoryValue, labelCategoryId: string): LabelDefinition {
  return {
    labelId: value.contentLabelCategoryValueId,
    labelCategoryId,
    name: value.name,
    filters: toFilterClauses(value),
    excludedContentIds: toExcludedContentIds(value),
  };
}

export function toLabelCategory(category: ContentLabelCategoryPayload): LabelCategory {
  return {
    labelCategoryId: category.contentLabelCategoryId,
    name: category.name,
    color: normalizeCategoryColor(category.color),
    // "Used for attribution" has no backend at all — it is cut from this slice
    // and pinned to false so nothing downstream has to handle `undefined`.
    usedForAttribution: false,
    labels: (category.values ?? []).map((value) => toLabelDefinition(value, category.contentLabelCategoryId)),
  };
}

export function toLabelCategories(categories: ContentLabelCategoryPayload[] | null | undefined): LabelCategory[] {
  return (categories ?? []).map(toLabelCategory);
}

// ── saving a rule ───────────────────────────────────────────────────────────

/**
 * Excluded ids as the numbers the exclusion clause carries: the same canonical
 * rule as `toAssignableContentIds` (see `toContentIdNumber`), but LENIENT — an id
 * that is not canonical is dropped instead of failing the save, because it can
 * only ever exclude the wrong content, and a stray id must not block saving the
 * rule it rides along with. Sorted ascending so saving the same set twice sends
 * the same filters.
 */
function toExclusionIds(excludedContentIds: string[] | null | undefined): number[] {
  const ids = (excludedContentIds ?? [])
    .map((contentId) => toContentIdNumber(String(contentId ?? '')))
    .filter((id): id is number => id !== null);
  return [...new Set(ids)].sort((a, b) => a - b);
}

/**
 * A label's draft as the value's `filters` on the wire: the usable rule clauses
 * (`toContentFilterInput`), plus ONE hidden `contentId ne [ids]` clause holding
 * its exclusions (decision 5).
 *
 * `null` — never `[]` — when nothing usable is left: `null` is "no rule" on
 * update (and the key is omitted on create), while an empty list has undefined
 * matcher semantics server-side. The exclusion clause is only ever sent WITH a
 * usable rule clause: on its own, `contentId ne [ids]` matches every other
 * content of the team, the opposite of a label without a rule.
 *
 * Exclusions are team-wide content ids, so the caller passes the label's whole
 * set, including ids outside the scope it is previewing — this function keeps
 * whatever it is handed.
 */
export function toLabelValueFiltersInput({
  filters,
  excludedContentIds,
}: {
  filters: LabelFilterClause[];
  excludedContentIds?: string[] | null;
}): NexoyaContentFilterInput[] | null {
  const rule = toContentFilterInput(filters);
  if (!rule.length) return null;

  const excluded = toExclusionIds(excludedContentIds);
  if (!excluded.length) return rule;

  return [
    ...rule,
    {
      fieldName: NexoyaContentFilterFieldName.ContentId,
      operator: NexoyaContentFilterOperator.Ne,
      value: { numberArr: excluded },
    },
  ];
}

// ── reading contents & their stage ──────────────────────────────────────────

/**
 * A `ContentV2` row as the `LabelableContent` the labels tables draw.
 *
 * - `contentId` is stringified: every selection, exclusion and manual assignment
 *   in the UI keys contents by `String(contentId)`, which is also exactly the
 *   form `toAssignableContentIds` accepts back.
 * - `bidStrategy` is the bidding strategy's TYPE, the value a bid-strategy rule
 *   keyword is written against.
 * - `providerName` is `toProviderDisplayName`'s answer, because the wire name is a
 *   translation key.
 */
export function toLabelableContent(
  content: LabelableContentPayload,
  translateProviderName: TranslateProviderName,
): LabelableContent {
  return {
    contentId: String(content.contentId),
    title: content.title ?? '',
    parentTitle: content.parent?.title ?? null,
    parentContentId: content.parent?.contentId == null ? null : String(content.parent.contentId),
    objective: content.objective ?? null,
    bidStrategy: content.biddingStrategy?.type ?? null,
    providerId: content.provider?.provider_id ?? null,
    providerName: toProviderDisplayName(content.provider?.name, translateProviderName),
  };
}

const STAGE_BY_STATUS: Record<NexoyaContentLabelStatus, LabelStage> = {
  [NexoyaContentLabelStatus.Labeled]: 'labeled',
  [NexoyaContentLabelStatus.NeedsDecision]: 'needsDecision',
  [NexoyaContentLabelStatus.Manual]: 'manual',
  [NexoyaContentLabelStatus.Unlabeled]: 'unlabeled',
};

const toValueIds = (values: LabelValueRefPayload[] | null | undefined): string[] =>
  (values ?? []).map((value) => value.contentLabelCategoryValueId);

/**
 * What one `ContentLabelAssignment` says about a (category, content) pair, with
 * the content itself left out.
 *
 * Separate from `toLabelAssignmentRow` because the SAME four fields are read
 * from two different documents: the stage pages, which carry a whole
 * `LabelableContent` per node, and `ContentV2.labelAssignments` on the portfolio
 * Content tab, where the content is the table row and the assignment carries
 * only its own state. `STAGE_BY_STATUS` is not exported — one mapping, reached
 * one way, so a second surface cannot quietly disagree about what MANUAL means.
 */
export type LabelAssignmentState = Pick<LabelAssignmentRow, 'stage' | 'matchIds' | 'appliedLabelId' | 'setByHand'>;

export function toAssignmentState(node: Omit<LabelAssignmentPayload, 'content'>): LabelAssignmentState {
  return {
    stage: STAGE_BY_STATUS[node.labelStatus] ?? 'unlabeled',
    matchIds: toValueIds(node.matches),
    appliedLabelId: node.assignment?.contentLabelCategoryValueId ?? null,
    setByHand: node.labelStatus === NexoyaContentLabelStatus.Manual,
  };
}

/**
 * One row of a category's stage table, as the server classified it. The stages
 * are disjoint, so everything the row shows follows from `labelStatus`:
 * `assignment` is filled for LABELED (the single rule match) and MANUAL (the
 * person's pick), `matches` lists the saved rules that reach the content in the
 * category's value order.
 */
export function toLabelAssignmentRow(
  node: LabelAssignmentPayload,
  translateProviderName: TranslateProviderName,
): LabelAssignmentRow {
  return {
    content: toLabelableContent(node.content, translateProviderName),
    ...toAssignmentState(node),
  };
}

/**
 * One row of the label sheet's preview: a content the DRAFT rule reaches.
 *
 * `labelAssignments` answers for every category of the team, so only this
 * category's entry counts. Competitors are that entry's SAVED-rule matches minus
 * the label being edited — its own saved rule matching is not a clash, it is the
 * previous version of the draft.
 */
export function toLabelPreviewRow(
  content: LabelPreviewContentPayload,
  category: LabelCategory,
  editingLabelId: string,
  translateProviderName: TranslateProviderName,
): LabelPreviewRow {
  const inCategory = (content.labelAssignments ?? []).find(
    (assignment) => assignment?.contentLabelCategory?.contentLabelCategoryId === category?.labelCategoryId,
  );

  const setByHand = inCategory?.labelStatus === NexoyaContentLabelStatus.Manual;

  return {
    content: toLabelableContent(content, translateProviderName),
    competitorIds: toValueIds(inCategory?.matches).filter((labelId) => labelId !== editingLabelId),
    setByHand,
    manualLabelId: setByHand ? (inCategory?.assignment?.contentLabelCategoryValueId ?? null) : null,
  };
}

/**
 * The coverage pills' numbers, keyed by `labelCategoryId`. The four stages are
 * disjoint server-side — one status per (category, content) — so their sum IS
 * the scope's total; there is no separate total to disagree with it.
 */
export function toLabelStageCountsById(
  categories: LabelStageCountsPayload[] | null | undefined,
): Record<string, LabelStageCounts> {
  return Object.fromEntries(
    (categories ?? []).map((category) => {
      const stageCounts = category.contentsByAssignmentStage?.counts;
      const countOf = (stage: LabelStage) => stageCounts?.[stage] ?? 0;
      const counts = {
        labeled: countOf('labeled'),
        needsDecision: countOf('needsDecision'),
        manual: countOf('manual'),
        unlabeled: countOf('unlabeled'),
      };

      return [
        category.contentLabelCategoryId,
        { ...counts, total: counts.labeled + counts.needsDecision + counts.manual + counts.unlabeled },
      ];
    }),
  );
}

/**
 * Either every selected id converted, or none of them are usable.
 *
 * Contract choice: a discriminated result rather than a throw or a
 * `{ ids, dropped }` pair.
 * - A `{ ids, dropped }` pair is still destructurable as `.ids` with `dropped`
 *   ignored, which is exactly the silent no-op this shape exists to prevent.
 * - Only the `assignable` branch carries `ids`, so TypeScript refuses to compile
 *   a call site that reaches for the converted list without handling the failure.
 * - Returning rather than throwing keeps the mapper usable by a UI that wants to
 *   *show* which contents cannot be labelled; the callers that cannot do
 *   anything useful with that (the mutation hooks) turn it into a rejection
 *   themselves.
 * - The failure branch deliberately does not carry the ids that did convert: a
 *   partially converted selection must never be assignable, because assigning
 *   49 of 50 contents and reporting success is the bug.
 *
 * The discriminant is a string rather than an `ok: boolean`, because this repo
 * compiles with `strictNullChecks: false`, under which TypeScript does not
 * narrow a boolean discriminant on the `false` branch — `if (!result.ok)` would
 * leave `result` un-narrowed and the guard unusable. A string literal narrows
 * either way round.
 */
export type AssignableContentIdsResult =
  | { status: 'assignable'; ids: number[] }
  | { status: 'unassignable'; unconvertible: string[] };

/** How many offending ids `unassignableContentIdsMessage` spells out before counting the rest. */
const UNCONVERTIBLE_IDS_IN_MESSAGE = 5;

/**
 * The only accepted form of a content id: exactly the string
 * `String(content.contentId)` produces for a positive integer JavaScript can
 * hold without losing a digit. Anything else is `null` — unconvertible — rather
 * than whatever `Number()` decides it means.
 *
 * `Number.isFinite` was not enough, because `Number()` is a *parser*, not a
 * validator, and several of its readings land on a real but different content:
 * - `'0x10'`, `'0b11'`, `'0o17'`, `'1e3'` — alternative literals. `'0x10'` would
 *   have been assigned against content 16. That is worse than the silent drop
 *   this guard was written to remove: it labels the wrong thing and reports
 *   success.
 * - `'007'`, `'+1'`, `'1.0'`, `'1.'` — non-canonical spellings of a number. They
 *   happen to name the right content, but they cannot come from
 *   `String(contentId)`, so an id shaped like that means the caller built it some
 *   other way and the rest of its digits cannot be trusted either.
 * - `'1.5'` — `contentIds` is `[Float!]!`, so a fraction serialises perfectly
 *   happily and matches nothing.
 * - `'0'`, `'-1'`, `'-0'` — content ids are database identities and start at 1.
 *   0 also has to go because it is where every malformed input degenerates:
 *   `Number('')`, `Number('  ')` and `Number(null)` are all 0, so rejecting
 *   non-positive values closes "assign against content 0" once, by value, rather
 *   than once per input shape that reaches it.
 * - Anything past `Number.MAX_SAFE_INTEGER` — `'9007199254740993'` parses to
 *   ...992, i.e. a neighbouring content, and even ids that survive the parse
 *   exactly (`'9007199254740994'`) sit in the range where a `Float` no longer
 *   round-trips digit-for-digit through JSON. The whole unsafe range is refused
 *   rather than silently losing precision; if content ids ever grow past 2^53
 *   this boundary has to become a string on the wire, and failing loudly is how
 *   we find out.
 *
 * Whitespace is deliberately NOT tolerated (`' 1 '` is unconvertible). Nothing in
 * the app pads an id — the contents table carries `String(contentId)` — so a
 * padded id is evidence it came from somewhere else, and trimming would be a
 * half-normalisation with no principled end (why `' 1 '` but not `'1 000'`?).
 * Rejecting is also safe to relax later; accepting is not.
 */
function toContentIdNumber(rawContentId: string): number | null {
  const parsed = Number(rawContentId);

  // Covers NaN, Infinity, fractions and the unsafe integer range in one check.
  if (!Number.isSafeInteger(parsed) || parsed <= 0) return null;
  // Canonical round-trip: the parse only counts if it reads back as the source.
  if (String(parsed) !== rawContentId) return null;

  return parsed;
}

/**
 * `manuallyAssignContentLabel` / `removeContentLabelManualAssignments` take
 * `contentIds: [Float!]!`, while every id in the UI domain is a string. This is
 * the only place the two meet, so the conversion is explicit here rather than
 * scattered `Number(...)` calls at each call site.
 *
 * An id that is not a canonical positive integer (see `toContentIdNumber`) is a
 * hard failure, not something to drop: `contentLabelCategoryValueId` travels as a
 * uuid on the very same input, so a uuid `contentId` is a live possibility, and
 * dropping them turns a total failure into a 200 that labelled nothing. An empty
 * selection fails too — `contentIds: []` is that same no-op by another route —
 * with an empty `unconvertible` list to say there is nothing to blame.
 *
 * De-duplicated because the contents table can hand back the same content from
 * more than one selection source.
 */
export function toAssignableContentIds(contentIds: string[] | null | undefined): AssignableContentIdsResult {
  const ids: number[] = [];
  const seen = new Set<number>();
  const unconvertible: string[] = [];

  (contentIds ?? []).forEach((contentId) => {
    const raw = String(contentId ?? '');
    const parsed = toContentIdNumber(raw);

    if (parsed === null) {
      unconvertible.push(raw);
      return;
    }
    if (seen.has(parsed)) return;

    seen.add(parsed);
    ids.push(parsed);
  });

  if (unconvertible.length > 0 || ids.length === 0) return { status: 'unassignable', unconvertible };

  return { status: 'assignable', ids };
}

/**
 * The one place the failure is turned into words, so the message stays covered
 * by the node-env tests instead of living untested inside a hook. Capped because
 * the repro is 50 selected uuids and nobody needs all of them in a snackbar.
 *
 * `action` is a parameter because both manual-assignment mutations share this
 * message and removal is not an assignment.
 */
export function unassignableContentIdsMessage(unconvertible: string[], action = 'assign content labels'): string {
  if (unconvertible.length === 0) return `Cannot ${action}: no contents are selected.`;

  const listed = unconvertible.slice(0, UNCONVERTIBLE_IDS_IN_MESSAGE).map((id) => JSON.stringify(id));
  const remaining = unconvertible.length - listed.length;
  const suffix = remaining > 0 ? `, and ${remaining} more` : '';

  // "not numeric" would be arguable — `Number('0x10')` is 16 — so the message
  // names the rule (`toContentIdNumber`) rather than a property the reader can
  // disprove in a console.
  return `Cannot ${action}: ${unconvertible.length} of the selected content ids are not whole positive numbers (${listed.join(', ')}${suffix}).`;
}
